import { Row, Col, Card, Typography, DatePicker, Divider, Select, ConfigProvider } from "antd";
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import moment from "moment";
import esES from "antd/lib/locale/es_ES";
import "moment/locale/es";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { BarChartOutlined } from "@ant-design/icons";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);
moment.locale("es");

const { RangePicker } = DatePicker;
const { Option } = Select;

/* ─────────────────────────────────────────────
   Preset date ranges
───────────────────────────────────────────── */
const TODAY = () => moment();

const DATE_PRESETS = [
  { label: "Hoy",             group: "Rápido",     range: () => [TODAY(), TODAY()] },
  { label: "Ayer",            group: "Rápido",     range: () => [TODAY().subtract(1,"day"), TODAY().subtract(1,"day")] },
  { label: "Últ. 7 días",    group: "Rápido",     range: () => [TODAY().subtract(6,"days"), TODAY()] },
  { label: "Últ. 30 días",   group: "Rápido",     range: () => [TODAY().subtract(29,"days"), TODAY()] },
  { label: "Últ. 90 días",   group: "Rápido",     range: () => [TODAY().subtract(89,"days"), TODAY()] },
  { label: "Sem. actual",    group: "Semana",     range: () => [TODAY().startOf("isoWeek"), TODAY().endOf("isoWeek")] },
  { label: "Sem. anterior",  group: "Semana",     range: () => [TODAY().subtract(1,"week").startOf("isoWeek"), TODAY().subtract(1,"week").endOf("isoWeek")] },
  { label: "Mes actual",     group: "Mes",        range: () => [TODAY().startOf("month"), TODAY().endOf("month")] },
  { label: "Mes anterior",   group: "Mes",        range: () => [TODAY().subtract(1,"month").startOf("month"), TODAY().subtract(1,"month").endOf("month")] },
  { label: "Trim. actual",   group: "Trimestre",  range: () => [TODAY().startOf("quarter"), TODAY().endOf("quarter")] },
  { label: "Trim. anterior", group: "Trimestre",  range: () => [TODAY().subtract(1,"quarter").startOf("quarter"), TODAY().subtract(1,"quarter").endOf("quarter")] },
  { label: "Año actual",     group: "Año",        range: () => [TODAY().startOf("year"), TODAY().endOf("year")] },
  { label: "Año anterior",   group: "Año",        range: () => [TODAY().subtract(1,"year").startOf("year"), TODAY().subtract(1,"year").endOf("year")] },
];

/* ─────────────────────────────────────────────
   Color palette per inspector index
───────────────────────────────────────────── */
const INSPECTOR_PALETTES = [
  { total: "#E6F1FB", totalText: "#0C447C", avg: "#E1F5EE", avgText: "#085041", max: "#FAEEDA", maxText: "#633806", min: "#EAF3DE", minText: "#27500A" },
  { total: "#EEEDFE", totalText: "#3C3489", avg: "#E1F5EE", avgText: "#085041", max: "#FAEEDA", maxText: "#633806", min: "#FAECE7", minText: "#712B13" },
  { total: "#FAECE7", totalText: "#712B13", avg: "#E6F1FB", avgText: "#0C447C", max: "#FAEEDA", maxText: "#633806", min: "#EAF3DE", minText: "#27500A" },
];

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */
function KpiCard({ label, value, subValue, bg, textColor }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        background: bg,
        borderRadius: 10,
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: "#5F5E5A" }}>
        {label}
      </span>
      <span style={{ fontSize: 22, fontWeight: 600, color: textColor, letterSpacing: "-0.5px", fontFamily: "'DM Mono', monospace", lineHeight: 1.15 }}>
        {value}
      </span>
      {subValue && (
        <span style={{ fontSize: 11, color: "#888780", marginTop: 2 }}>{subValue}</span>
      )}
    </motion.div>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography.Text style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px", color: "#888780", display: "block", marginBottom: 14 }}>
      {children}
    </Typography.Text>
  );
}

function formatMXN(value) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(value ?? 0);
}

function formatCount(value) {
  return new Intl.NumberFormat("es-MX").format(value ?? 0);
}

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
const CARD = {
  background: "#fff",
  border: "0.5px solid rgba(0,0,0,0.08)",
  borderRadius: "12px",
};

export default function Stats() {
  const defaultRange = [moment().subtract(1, "month"), moment()];
  const [dateRange, setDateRange] = useState(defaultRange);
  const [activePreset, setActivePreset] = useState(null);
  const [groupBy, setGroupBy] = useState("day");

  const [stats, setStats] = useState({ CompletasCount: 0, IncompletasCount: 0, CompletasMonto: 0, IncompletasMonto: 0 });
  const [uvieSummary, setUvieSummary] = useState([]);
  const [earningsData, setEarningsData] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ── API calls ── */
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/verificaciones/costos`, {
      params: { rango1: dateRange[0].format("YYYY-MM-DD"), rango2: dateRange[1].format("YYYY-MM-DD") },
    }).then(res => { if (res.data?.[0]) setStats(res.data[0]); }).catch(console.error);
  }, [dateRange]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/uvies`).then(res => setUvieSummary(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/earnings`, {
      params: { rango1: dateRange[0].format("YYYY-MM-DD"), rango2: dateRange[1].format("YYYY-MM-DD"), groupBy },
    }).then(res => {
      const fullData = [];
      let current = dateRange[0].clone();
      if (groupBy === "week") current = current.startOf("isoWeek");
      else if (groupBy === "month") current = current.startOf("month");

      while (current.isSameOrBefore(dateRange[1])) {
        let periodKey = groupBy === "day" ? current.format("YYYY-MM-DD") : groupBy === "week" ? current.format("YYYY-MM-DD") : current.format("YYYY-MM");
        const existing = res.data.find(i => i.period === periodKey);
        fullData.push({ period: periodKey, earnings: existing?.earnings ?? 0 });
        current.add(1, groupBy);
      }
      setEarningsData(fullData);
    }).catch(console.error);
  }, [dateRange, groupBy]);

  /* ── Chart data ── */
  const barData = {
    labels: earningsData.map(item => {
      if (groupBy === "day") return moment(item.period).format("DD/MM");
      if (groupBy === "week") return "Sem " + moment(item.period).format("DD/MM");
      return moment(item.period).format("MMM YY");
    }),
    datasets: [{
      label: "Ganancias",
      data: earningsData.map(i => i.earnings),
      backgroundColor: "#378ADD",
      borderRadius: 6,
      borderSkipped: false,
    }],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => formatMXN(ctx.parsed.y) } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: "#888780", maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
      },
      y: {
        grid: { color: "rgba(0,0,0,0.05)" },
        border: { display: false },
        ticks: {
          font: { size: 11 },
          color: "#888780",
          callback: v => "$" + (v >= 1000 ? (v / 1000).toFixed(0) + "k" : v),
        },
      },
    },
  };

  const pieData = {
    labels: ["Completas", "Incompletas"],
    datasets: [{
      data: [stats.CompletasCount, stats.IncompletasCount],
      backgroundColor: ["#378ADD", "#E24B4A"],
      borderColor: "#fff",
      borderWidth: 3,
      hoverOffset: 6,
    }],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: { legend: { display: false } },
  };

  /* ── Preset handler ── */
  function applyPreset(preset) {
    const [start, end] = preset.range();
    setDateRange([start.clone(), end.clone()]);
    setActivePreset(preset.label);
  }

  /* ── Grouped presets ── */
  const presetGroups = DATE_PRESETS.reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p);
    return acc;
  }, {});

  const groupKeys = ["Rápido", "Semana", "Mes", "Trimestre", "Año"];

  /* ── Styles ── */
  const S = {
    root: { fontFamily: "'DM Sans', sans-serif", background: "#F5F4F1", minHeight: "100vh" },
    header: {
      background: "#fff",
      borderBottom: "1px solid #E8E6E0",
      padding: "20px 32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
    },
    presetsBar: {
      background: "#fff",
      borderBottom: "1px solid #E8E6E0",
      padding: "10px 32px",
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      overflowX: "auto",
    },
    body: { padding: "28px 32px", display: "flex", flexDirection: "column", gap: 28 },
    inspectorCard: {
      background: "#fff",
      borderRadius: 12,
      border: "1px solid #E8E6E0",
      padding: "20px 24px",
      marginBottom: 16,
    },
    periodCard: {
      background: "#fff",
      borderRadius: 12,
      border: "1px solid #E8E6E0",
      overflow: "hidden",
    },
    chartCard: {
      background: "#F5F4F1",
      borderRadius: 10,
      padding: 18,
    },
  };

  const presetBtnBase = {
    background: "transparent",
    border: "1px solid #D3D1C7",
    borderRadius: 6,
    padding: "5px 10px",
    fontSize: 12,
    color: "#5F5E5A",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.12s",
    fontFamily: "inherit",
  };

  const presetBtnActive = { ...presetBtnBase, background: "#378ADD", borderColor: "#185FA5", color: "#fff", fontWeight: 500 };

  const groupByBtnBase = {
    background: "transparent",
    border: "1px solid #D3D1C7",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 11,
    color: "#888780",
    cursor: "pointer",
    fontFamily: "inherit",
  };

  const groupByBtnActive = { ...groupByBtnBase, background: "#1A1916", borderColor: "#1A1916", color: "#fff" };

    return (
      <ConfigProvider locale={esES}>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {/* ── Header card ── */}
          <div style={{ ...CARD, padding: 0 }}>
            {/* Title row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                padding: "18px 20px 14px",
                borderBottom: "0.5px solid rgba(0,0,0,0.06)",
                flexWrap: "wrap",
              }}
            >
              {/* Icon badge */}
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "rgba(24,144,255,0.08)",
                  border: "0.5px solid rgba(24,144,255,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <BarChartOutlined style={{ fontSize: 17, color: "#1677ff" }} />
              </div>
  
              {/* Title + subtitle */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "rgba(0,0,0,0.88)", lineHeight: 1.3 }}>
                  Estadísticas
                </div>
                <div style={{ fontSize: 12, color: "rgba(0,0,0,0.38)", marginTop: 1 }}>
                  Resumen de actividad y rendimiento del sistema
                </div>
              </div>
  
              {/* Actions: Range Picker */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.45)", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                  Período
                </span>
                <RangePicker
                  value={dateRange}
                  format="DD/MM/YYYY"
                  onChange={(dates) => {
                    if (dates) {
                      setDateRange(dates);
                      setActivePreset(null);
                    }
                  }}
                  style={{
                    borderRadius: 8,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 13,
                  }}
                />
              </div>
            </div>
  
            {/* Preset row */}
            <div style={{ padding: "12px 20px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", overflowX: "auto" }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#888780", textTransform: "uppercase", letterSpacing: "0.5px", marginRight: 4, flexShrink: 0 }}>
                Filtros rápidos:
              </span>
              {groupKeys.map((groupKey, gi) => (
                <div key={groupKey} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {gi > 0 && <div style={{ width: 1, height: 16, background: "rgba(0,0,0,0.06)", margin: "0 4px" }} />}
                  {(presetGroups[groupKey] || []).map((preset) => (
                    <button
                      key={preset.label}
                      style={activePreset === preset.label ? presetBtnActive : presetBtnBase}
                      onClick={() => applyPreset(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
  
          {/* ── BODY ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {/* ── INSPECTORS ── */}
            {uvieSummary.length > 0 && (
              <div>
                <SectionTitle>Verificadores</SectionTitle>
                {uvieSummary.map((uvie, index) => {
                  const palette = INSPECTOR_PALETTES[index % INSPECTOR_PALETTES.length];
                  return (
                    <motion.div
                      key={uvie.NumeroUvie}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.08, duration: 0.35 }}
                      style={{ ...S.inspectorCard, ...CARD, padding: "20px 24px" }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
                        <div>
                          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600, color: "#1A1916", letterSpacing: "-0.2px" }}>
                            {uvie.NombreCompleto}
                          </Typography.Title>
                          <span style={{ fontSize: 12, color: "#888780", fontFamily: "'DM Mono', monospace" }}>
                            UVSEIE-{uvie.NumeroUvie}
                          </span>
                        </div>
                        <span style={{ background: "#EAF3DE", color: "#3B6D11", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 500 }}>
                          Activo
                        </span>
                      </div>
  
                      <Row gutter={[12, 12]}>
                        <Col xl={6} lg={12} md={12} sm={12} xs={12}>
                          <KpiCard label="Total acumulado" value={formatMXN(uvie.TotalMonto)} subValue={`en ${formatCount(uvie.TotalVerificaciones)} verif.`} bg={palette.total} textColor={palette.totalText} />
                        </Col>
                        <Col xl={6} lg={12} md={12} sm={12} xs={12}>
                          <KpiCard label="Promedio monto" value={formatMXN(uvie.PromedioMonto)} bg={palette.avg} textColor={palette.avgText} />
                        </Col>
                        <Col xl={6} lg={12} md={12} sm={12} xs={12}>
                          <KpiCard label="Monto máximo" value={formatMXN(uvie.MaxMonto)} bg={palette.max} textColor={palette.maxText} />
                        </Col>
                        <Col xl={6} lg={12} md={12} sm={12} xs={12}>
                          <KpiCard label="Monto mínimo" value={formatMXN(uvie.MinMonto)} bg={palette.min} textColor={palette.minText} />
                        </Col>
                      </Row>
                    </motion.div>
                  );
                })}
              </div>
            )}
  
            {/* ── PERIOD STATS ── */}
            <div style={{ ...S.periodCard, ...CARD }}>
              <div style={{ padding: "18px 24px 14px", borderBottom: "1px solid #F1EFE8", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <Typography.Text style={{ fontSize: 14, fontWeight: 600, color: "#1A1916" }}>
                  Estadísticas del período
                </Typography.Text>
                <span style={{ fontSize: 12, color: "#888780", fontFamily: "'DM Mono', monospace" }}>
                  {dateRange[0].format("DD/MM/YYYY")} – {dateRange[1].format("DD/MM/YYYY")}
                </span>
              </div>
  
              <div style={{ padding: "20px 24px" }}>
                <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
                  {[
                    { label: "Verif. completas", value: formatCount(stats.CompletasCount), bg: "#E6F1FB", textColor: "#0C447C" },
                    { label: "Verif. incompletas", value: formatCount(stats.IncompletasCount), bg: "#FAECE7", textColor: "#712B13" },
                    { label: "Monto completas", value: formatMXN(stats.CompletasMonto), bg: "#E1F5EE", textColor: "#085041" },
                    { label: "Monto incompletas", value: formatMXN(stats.IncompletasMonto), bg: "#FAEEDA", textColor: "#633806" },
                  ].map((item, i) => (
                    <Col key={i} xl={6} lg={12} md={12} sm={12} xs={12}>
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
                        <KpiCard {...item} />
                      </motion.div>
                    </Col>
                  ))}
                </Row>
  
                <Row gutter={[16, 16]}>
                  {/* Bar chart */}
                  <Col xl={16} lg={24} md={24} sm={24} xs={24}>
                    <div style={S.chartCard}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: "#444441" }}>
                          Ganancias en el período
                        </span>
                        <div style={{ display: "flex", gap: 4 }}>
                          {[{ key: "day", label: "Día" }, { key: "week", label: "Semana" }, { key: "month", label: "Mes" }].map(opt => (
                            <button
                              key={opt.key}
                              style={groupBy === opt.key ? groupByBtnActive : groupByBtnBase}
                              onClick={() => setGroupBy(opt.key)}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div style={{ height: 240, position: "relative" }}>
                        <Bar data={barData} options={barOptions} />
                      </div>
                    </div>
                  </Col>
  
                  {/* Pie chart */}
                  <Col xl={8} lg={24} md={24} sm={24} xs={24}>
                    <div style={{ ...S.chartCard, height: "100%", boxSizing: "border-box" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: "#444441", display: "block", marginBottom: 12 }}>
                        Estado de verificaciones
                      </span>
                      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12, fontSize: 12, color: "#5F5E5A" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#378ADD" }} />
                          Completas
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#E24B4A" }} />
                          Incompletas
                        </span>
                      </div>
                      <div style={{ height: 200, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Doughnut data={pieData} options={pieOptions} />
                      </div>
                      <div style={{ textAlign: "center", marginTop: 8 }}>
                        <span style={{ fontSize: 13, color: "#5F5E5A" }}>
                          Tasa de éxito:{" "}
                          <strong style={{ color: "#378ADD" }}>
                            {stats.CompletasCount + stats.IncompletasCount > 0
                              ? ((stats.CompletasCount / (stats.CompletasCount + stats.IncompletasCount)) * 100).toFixed(1) + "%"
                              : "—"}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </div>
      </ConfigProvider>
    );
  }
  